import { Box, SegmentedControl, Skeleton, Text } from '@mantine/core';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { isDefined } from 'remeda';
import {
  type CohortGrouping,
  useLoadCollectionCohorts,
} from 'shared/hooks/use-load-collection-cohorts';

import { AnalyticsCard } from '../AnalyticsCard';
import { CollectionCohortsTable } from '../charts/CollectionCohortsTable';
import { toCohortGrid } from '../charts/collection-cohorts-grid';

type CollectionCohortsCardProps = {
  periodMonths: number;
};

const LABELS = i18nKeys.ANALYTICS.COLLECTION_COHORTS;

export const CollectionCohortsCard = ({ periodMonths }: CollectionCohortsCardProps) => {
  const { t } = useTranslation();
  const [groupBy, setGroupBy] = useState<CohortGrouping>('issue_date');
  const { collectionCohorts, isCollectionCohortsLoading } = useLoadCollectionCohorts({
    periodMonths,
    groupBy,
  });

  return (
    <AnalyticsCard
      title={t(LABELS.TITLE)}
      helpContent={<CollectionCohortsHelp />}
      actions={
        <SegmentedControl
          size="xs"
          value={groupBy}
          onChange={value => setGroupBy(value === 'due_date' ? 'due_date' : 'issue_date')}
          data={[
            { label: t(LABELS.GROUP_BY.ISSUE_DATE), value: 'issue_date' },
            { label: t(LABELS.GROUP_BY.DUE_DATE), value: 'due_date' },
          ]}
        />
      }
    >
      {isCollectionCohortsLoading && <Skeleton h={180} mt="md" radius={8} aria-busy />}
      {isDefined(collectionCohorts) && (
        <CollectionCohortsTable
          grid={toCohortGrid(collectionCohorts)}
          currency={collectionCohorts.currency}
        />
      )}
    </AnalyticsCard>
  );
};

const CollectionCohortsHelp = () => {
  const { t } = useTranslation();

  return (
    <Box>
      <Text fw="bold">{t(LABELS.TITLE)}</Text>
      <br />
      <Text>{t(LABELS.HELP.LINE_1)}</Text>
      <br />
      <Text>{t(LABELS.HELP.LINE_2)}</Text>
      <br />
      <Text>{t(LABELS.HELP.LINE_3)}</Text>
      <br />
      <Text>{t(LABELS.HELP.LINE_4)}</Text>
      <br />
      <Text>{t(LABELS.HELP.LINE_5)}</Text>
    </Box>
  );
};
